import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { COMPANY } from "@/lib/api";
import { COMMERCIAL_IMAGES } from "@/data/images";

const truckStats = [
  { k: "50", v: "States Served" },
  { k: "10,000+ lbs", v: "Typical Payload" },
  { k: "Liftgate", v: "Standard on Fleet" },
  { k: "Pallet Jack", v: "Included with Every Trip" },
];

const stagger = { animate: { transition: { staggerChildren: 0.06 } } };
const rise = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0, transition: { duration: 0.55 } } };

export function CommercialHero() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden pt-24 pb-16 lg:pb-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="initial" animate="animate" variants={stagger} className="lg:col-span-6">
            <motion.div variants={rise} className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">
              Commercial Deliveries
            </motion.div>
            <motion.h1 variants={rise} className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-slate-900">
              Freight your business can<br />
              <span className="text-primary-brand">count on. Safely.</span>
            </motion.h1>
            <motion.p variants={rise} className="mt-6 text-slate-700 leading-relaxed max-w-xl">
              Scheduled and on-demand trucking deliveries for retailers, distributors, warehouses,
              and enterprises across all 50 states. Every mile is planned, every load is secured,
              and every driver is trained to protect your freight and your reputation.
            </motion.p>
            <motion.div variants={rise} className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/quote"
                data-testid="commercial-quote-cta-hero"
                className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm"
              >
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="commercial-call-cta"
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-primary-brand hover:text-primary-brand text-slate-900 px-7 py-4 font-bold uppercase tracking-wider text-sm"
              >
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }} className="lg:col-span-6 relative">
            <img
              src={COMMERCIAL_IMAGES.fleetYard}
              alt="Laronta Pro fleet of trucks staged at warehouse"
              data-testid="commercial-hero-image"
              className="w-full h-auto object-cover border border-slate-200"
            />
            <div className="absolute -bottom-5 -left-5 hidden md:block bg-white border border-primary-brand px-5 py-4 shadow-sm">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary-brand">Fleet Ready</div>
              <div className="font-heading font-black text-slate-900 text-lg mt-1">Dispatched daily<br/>across the country.</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200">
          {truckStats.map((s) => (
            <div key={s.v} className="bg-white px-6 py-8">
              <div className="font-heading text-2xl md:text-3xl font-black text-primary-brand">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
